import FormControlLabel from '@mui/material/FormControlLabel';
import Stack from '@mui/material/Stack';

import { CheckboxListItems } from './Items';
import { StateCheckbox } from './StateCheckbox';
import {
  computeCheckboxListAllState,
  hasCheckboxListToggleableItems,
  toggleCheckboxListItems,
} from './utils/checkbox-list-state.util';
import type { CheckboxListItem } from './types/checkbox-list-item.type';

export type { CheckboxListItem };

interface Props<I extends string> {
  readonly childrenRequireParent?: boolean;
  readonly disabled?: boolean;
  readonly idPrefix: string;
  readonly items: readonly CheckboxListItem<I>[];
  readonly onChange?: (value: readonly I[]) => void;
  readonly selectAllLabel?: string;
  readonly value: readonly I[];
  readonly variant: 'consultation' | 'edition';
}

// One component for every list of checkboxes: read-only in consultation; in
// edition, a "Sélectionner tout" entry first, and every item with children
// checks or unchecks its whole subtree (indeterminate when partly checked).
export const CheckboxList = <I extends string>({
  childrenRequireParent = false,
  disabled = false,
  idPrefix,
  items,
  onChange,
  selectAllLabel = 'Sélectionner tout',
  value,
  variant,
}: Readonly<Props<I>>) => {
  const isEditable = variant === 'edition' && !disabled && !!onChange;

  const handleToggle = (toggledItems: readonly CheckboxListItem<I>[]) => {
    onChange?.(toggleCheckboxListItems(toggledItems, value));
  };

  return (
    <Stack>
      {variant === 'edition' && (
        <FormControlLabel
          control={
            <StateCheckbox
              disabled={!isEditable || !hasCheckboxListToggleableItems(items)}
              id={`${idPrefix}select-all`}
              // eslint-disable-next-line react/jsx-no-bind
              onChange={() => {
                handleToggle(items);
              }}
              state={computeCheckboxListAllState(items, value)}
            />
          }
          label={selectAllLabel}
        />
      )}
      <CheckboxListItems
        childrenRequireParent={childrenRequireParent}
        depth={0}
        idPrefix={idPrefix}
        isEditable={isEditable}
        isParentChecked
        items={items}
        // eslint-disable-next-line react/jsx-no-bind
        onToggle={handleToggle}
        value={value}
      />
    </Stack>
  );
};
